import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { admin, type AdminRole, type AdminRoleRow } from "@/lib/admin";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/Button";
import { useAdminRole, canManageAdmins } from "@/hooks/useAdminRole";

// ────────────────────────────────────────────────────────────────────────
// Admin role management page — super_admin only.
// ────────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ROLE_DESCRIPTIONS: Record<AdminRole, { title: string; sub: string; tone: string }> = {
  super_admin: {
    title: "Super admin",
    sub: "Full access. Can manage admins, change permissions, delete users, and manage coupons.",
    tone: "bg-fh-green/15 text-fh-green-dark border-fh-green/30",
  },
  admin: {
    title: "Admin",
    sub: "Day-to-day patient ops: reset passwords, change emails, archive accounts. No role management.",
    tone: "bg-fh-sand text-fh-ink border-fh-border",
  },
  viewer: {
    title: "Viewer",
    sub: "Read-only. Can see users, analytics, and coupon data. Cannot make changes.",
    tone: "bg-fh-cream text-fh-gray border-fh-border",
  },
};

export default function AdminAdmins() {
  const { role, loading } = useAdminRole();
  const qc = useQueryClient();
  const [pending, setPending] = useState<
    | { kind: "remove"; admin: AdminRoleRow }
    | { kind: "change"; admin: AdminRoleRow; newRole: AdminRole }
    | { kind: "add" }
    | null
  >(null);
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  // Add-admin form state
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<AdminRole>("viewer");
  const [addNotes, setAddNotes] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const adminsQ = useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () => admin.listAdmins(),
    enabled: canManageAdmins(role),
  });

  if (loading) return <AdminLayout><LoadingScreen /></AdminLayout>;
  if (!canManageAdmins(role)) {
    // Bounce non-super-admins back to the users tab. They shouldn't see this URL.
    return <Navigate to="/admin/users" replace />;
  }

  async function runRemove() {
    if (!pending || pending.kind !== "remove") return;
    setWorking(true);
    try {
      await admin.removeAdminRole(pending.admin.user_id);
      setNotice({ tone: "success", text: `Removed admin access from ${pending.admin.email}` });
      await qc.invalidateQueries({ queryKey: ["admin", "admins"] });
      setPending(null);
    } catch (e) {
      setNotice({ tone: "error", text: (e as Error).message });
    } finally {
      setWorking(false);
    }
  }

  async function runChange() {
    if (!pending || pending.kind !== "change") return;
    setWorking(true);
    try {
      await admin.setAdminRole(pending.admin.user_id, pending.newRole);
      setNotice({
        tone: "success",
        text: `${pending.admin.email} is now ${ROLE_DESCRIPTIONS[pending.newRole].title}`,
      });
      await qc.invalidateQueries({ queryKey: ["admin", "admins"] });
      setPending(null);
    } catch (e) {
      setNotice({ tone: "error", text: (e as Error).message });
    } finally {
      setWorking(false);
    }
  }

  async function runAdd() {
    setAddError(null);
    if (!addEmail.trim()) { setAddError("Email required"); return; }
    setWorking(true);
    try {
      // grantAdminByEmail handles both cases: existing user (just sets
      // the role) and brand-new email (creates the auth user + sends
      // an invite-style magic link, then sets the role). No more
      // "must already exist" failure mode.
      const result = await admin.grantAdminByEmail(
        addEmail.trim(), addRole, addNotes.trim() || undefined,
      );
      setNotice({
        tone: "success",
        text: result.was_created
          ? `Invite sent to ${addEmail.trim()} — they'll get a sign-in link by email. Role: ${ROLE_DESCRIPTIONS[addRole].title}.`
          : `${addEmail.trim()} granted ${ROLE_DESCRIPTIONS[addRole].title} role`,
      });
      await qc.invalidateQueries({ queryKey: ["admin", "admins"] });
      setPending(null);
      setAddEmail("");
      setAddRole("viewer");
      setAddNotes("");
    } catch (e) {
      setAddError((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  const admins = adminsQ.data?.admins ?? [];
  const sorted = [...admins].sort((a, b) => {
    // Super admins first, then admin, then viewer; within tier, alpha by email
    const order: Record<AdminRole, number> = { super_admin: 0, admin: 1, viewer: 2 };
    if (order[a.role] !== order[b.role]) return order[a.role] - order[b.role];
    return (a.email ?? "").localeCompare(b.email ?? "");
  });

  return (
    <AdminLayout>
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-[28px] text-fh-ink leading-tight mb-1">Admin permissions</h1>
          <p className="text-[14px] text-fh-gray m-0">
            {admins.length} admin{admins.length === 1 ? "" : "s"}. Super admins can grant/revoke access for anyone.
          </p>
        </div>
        <Button onClick={() => { setAddError(null); setPending({ kind: "add" }); }}>
          + Add admin
        </Button>
      </header>

      {notice && (
        <div className="mb-4">
          <Alert tone={notice.tone}>
            <div className="flex items-center justify-between gap-3">
              <span>{notice.text}</span>
              <button type="button" onClick={() => setNotice(null)} className="text-[12px] font-semibold underline">
                Dismiss
              </button>
            </div>
          </Alert>
        </div>
      )}

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {(["super_admin", "admin", "viewer"] as const).map((r) => (
          <div key={r} className={`rounded-[12px] border p-3.5 ${ROLE_DESCRIPTIONS[r].tone}`}>
            <div className="font-semibold text-[14px] mb-1">{ROLE_DESCRIPTIONS[r].title}</div>
            <div className="text-[12.5px] leading-relaxed">{ROLE_DESCRIPTIONS[r].sub}</div>
          </div>
        ))}
      </div>

      {adminsQ.isLoading ? (
        <LoadingScreen />
      ) : adminsQ.error ? (
        <Alert tone="error">{(adminsQ.error as Error).message}</Alert>
      ) : (
        <Card>
          <div className="-mx-[22px] -my-[22px] overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead className="text-left text-[12px] uppercase tracking-wider text-fh-gray bg-fh-cream border-b border-fh-border">
                <tr>
                  <th className="px-4 py-3 font-semibold">Admin</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Granted</th>
                  <th className="px-4 py-3 font-semibold">Last sign-in</th>
                  <th className="px-4 py-3 font-semibold">Notes</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <tr key={a.user_id} className="border-b border-fh-border last:border-0 hover:bg-fh-cream/60">
                    <td className="px-4 py-3 text-fh-ink">{a.email ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[12px] font-semibold px-2 py-0.5 rounded border ${ROLE_DESCRIPTIONS[a.role].tone}`}>
                        {ROLE_DESCRIPTIONS[a.role].title}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{fmtDate(a.granted_at)}</td>
                    <td className="px-4 py-3 text-fh-gray whitespace-nowrap">{relativeTime(a.last_sign_in_at)}</td>
                    <td className="px-4 py-3 text-fh-gray text-[12.5px]">{a.notes ?? "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <select
                          value={a.role}
                          onChange={(e) => {
                            const newRole = e.target.value as AdminRole;
                            if (newRole !== a.role) setPending({ kind: "change", admin: a, newRole });
                          }}
                          className="rounded-[8px] border border-fh-border bg-white px-2 py-1 text-[12.5px] text-fh-ink"
                        >
                          <option value="super_admin">Super admin</option>
                          <option value="admin">Admin</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setPending({ kind: "remove", admin: a })}
                          className="text-[12px] font-semibold text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={pending?.kind === "remove"}
        title="Revoke admin access?"
        body={`${pending?.kind === "remove" ? pending.admin.email : ""} will lose all admin access. They can still use their patient account if they have one. You cannot remove yourself, and you cannot remove the last super admin.`}
        confirmLabel="Revoke access"
        destructive
        loading={working}
        onConfirm={runRemove}
        onCancel={() => setPending(null)}
      />

      <ConfirmDialog
        open={pending?.kind === "change"}
        title="Change admin role?"
        body={`${pending?.kind === "change" ? pending.admin.email : ""} will be changed to ${pending?.kind === "change" ? ROLE_DESCRIPTIONS[pending.newRole].title : ""}. ${pending?.kind === "change" ? ROLE_DESCRIPTIONS[pending.newRole].sub : ""}`}
        confirmLabel="Apply change"
        loading={working}
        onConfirm={runChange}
        onCancel={() => setPending(null)}
      />

      {/* Add-admin dialog (custom form, can't use PromptDialog because it has 3 fields) */}
      {pending?.kind === "add" && (
        <div className="fixed inset-0 bg-fh-ink/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[14px] shadow-card max-w-[480px] w-full p-6">
            <h2 className="font-serif text-[22px] text-fh-ink mb-1">Grant admin access</h2>
            <p className="text-[13px] text-fh-gray mb-4 leading-relaxed">
              If the person already has a Forte Health account, we just add admin permissions on top of
              their existing profile. If they don't, we'll create the account and email them a one-click
              sign-in link to set up their password.
            </p>
            <label className="block text-[12.5px] font-semibold text-fh-ink mb-1">Email</label>
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="someone@fortehealth.co"
              className="w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-2 text-[14px] mb-3 focus:outline-none focus:border-fh-green"
            />
            <label className="block text-[12.5px] font-semibold text-fh-ink mb-1">Role</label>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as AdminRole)}
              className="w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-2 text-[14px] mb-3"
            >
              <option value="viewer">Viewer — read-only</option>
              <option value="admin">Admin — day-to-day ops</option>
              <option value="super_admin">Super admin — full access</option>
            </select>
            <label className="block text-[12.5px] font-semibold text-fh-ink mb-1">Notes (optional)</label>
            <input
              type="text"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              placeholder="e.g. Ops contractor, contract ends 2026-08"
              className="w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-2 text-[14px] mb-3"
            />
            {addError && (
              <div className="mb-3"><Alert tone="error">{addError}</Alert></div>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={() => setPending(null)}
                disabled={working}
                className="px-3.5 py-2 text-[13.5px] font-semibold text-fh-gray hover:text-fh-ink"
              >
                Cancel
              </button>
              <Button onClick={runAdd} loading={working}>Grant access</Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
